import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { FiArrowDown } from 'react-icons/fi';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';


const ProjectDetail = () => {
    const { id } = useParams();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    // Définir une position par défaut pour la carte (par exemple, Aix-en-Provence)
    const defaultLocation = [43.529742, 5.447427];

    useEffect(() => {
        const fetchProject = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/projets/${id}?populate=*`, {
                    headers: {
                        Authorization: `${process.env.REACT_APP_API_TOKEN}`
                    }
                });
                setProject(response.data.data);
                setLoading(false);
            } catch (err) {
                setError(err);
                setLoading(false);
            }
        };

        fetchProject();
    }, [id]);

    if (loading) return <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900"></div>
    </div>;
    if (error) return <div>Error: {error.message}</div>;
    if (!project) return <div>Projet non trouvé</div>;

    // eslint-disable-next-line
    // const { title, type, year, location, content, image, materials, challenges, additionalImages } = project;
    const {
        title = 'Titre du projet',
        type = 'Type de projet',
        year = 'Année de réalisation',
        location = 'Lieu de réalisation',
        content = [],
        image = { url: '/placeholder-image.jpg' },
        materials = [],
        challenges = [],
        additionalImages = [],
    } = project;
    const ImageUrl = `${project.image.url}`;
    const AdditionalImagesUrls = project.additionalImages.map(image => {
        return `${image.url}`;
    });

    // Divide images into columns for masonry layout
    const columns = 4;
    const masonryColumns = Array.from({ length: columns }, () => []);

    AdditionalImagesUrls.forEach((url, index) => {
        masonryColumns[index % columns].push(url);
    });

    const projectLocation = project?.map
        ? [parseFloat(project.map.lat), parseFloat(project.map.lng)]
        : defaultLocation;

    const Block = ({ className, children }) => (
        <motion.div
            variants={{
                initial: { scale: 0.5, y: 50, opacity: 0 },
                animate: { scale: 1, y: 0, opacity: 1 },
            }}
            transition={{ type: 'spring', mass: 3, stiffness: 400, damping: 50 }}
            className={`col-span-4 rounded-lg border border-cocoa-600 bg-cocoa p-6 ${className}`}
        >
            {children}
        </motion.div>
    );

    return (
        <div className="min-h-screen bg-cream">
            {/* Hero Section */}
            <div className="relative h-screen">
                <img src={ImageUrl} alt={title} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <h1 className="text-4xl font-bold text-cream uppercase">{title}</h1>
                </div>
                <div className="absolute bottom-4 left-4">
                    <Link to="/design-and-projects" className="inline-block bg-cocoa text-cream py-2 px-4 rounded shadow-md hover:bg-brandy-rose transition-colors duration-300">Retour à la liste des projets</Link>
                </div>
                <div className="absolute bottom-4 right-4">
                    <a href="#content" className="inline-block bg-cocoa text-cream p-2 rounded-full shadow-md hover:bg-brandy-rose transition-colors duration-300">
                        <FiArrowDown className="text-2xl" />
                    </a>
                </div>
            </div>

            {/* Content Section */}
            <section className="max-w-4xl mx-auto px-4 py-16">
                {/* Description */}
                <div className="prose prose-lg mx-auto">
                    {project.description && project.description.map((block, index) => {
                        switch (block.type) {
                            case 'paragraph':
                                return (
                                    <div key={index} className="mb-8">
                                        {block.children && block.children.map((child, childIndex) => {
                                            switch (child.type) {
                                                case 'text':
                                                    return (
                                                        <span
                                                        key={childIndex}
                                                            className={`
                                                    text-gray-600
                                                    ${child.bold ? 'font-bold' : ''}
                                                    ${child.italic ? 'italic' : ''}
                                                    ${child.underline ? 'underline' : ''}
                                                `}
                                                        >
                                                            {child.text}
                                                        </span>
                                                    );
                                                case 'link':
                                                    return (
                                                        <a
                                                            key={childIndex}
                                                            href={child.url}
                                                            className="text-cocoa hover:text-brandy-rose transition-colors underline"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {child.children[0].text}
                                                        </a>
                                                    );
                                                default:
                                                    return null;
                                            }
                                        })}
                                    </div>
                                );
                            case 'heading':
                                return (
                                    <h2 key={index} className="text-3xl font-cormorant text-cocoa mb-6">
                                        {block.children[0].text}
                                    </h2>
                                );
                            case 'list':
                                return (
                                    <ul key={index} className="list-disc pl-6 mb-6 text-gray-600">
                                        {block.children.map((item, itemIndex) => (
                                            <li key={itemIndex} className="mb-2">
                                                {item.children[0].text}
                                            </li>
                                        ))}
                                    </ul>
                                );
                            default:
                                return null;
                        }
                    })}
                </div>

                {/* Image Gallery */}
                {additionalImages && additionalImages.length > 0 && (
                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                    >
                        {additionalImages.map((image, index) => (
                            <motion.div
                                key={index}
                                className="aspect-w-4 aspect-h-3 overflow-hidden rounded-lg"
                                whileHover={{ scale: 1.02 }}
                                transition={{ duration: 0.2 }}
                                onClick={() => setSelectedImage(image.formats?.large?.url || image.url)}
                            >
                                <img
                                    src={image.formats?.large?.url || image.url}
                                    alt={`Vue ${index + 1}`}
                                    className="w-full h-full object-cover"
                                />
                            </motion.div>
                        ))}
                    </motion.div>
                )}

                {/* Image Modal */}
                {selectedImage && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
                        onClick={() => setSelectedImage(null)}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            className="relative max-w-7xl max-h-[90vh] w-full h-full"
                        >
                            <img
                                src={selectedImage}
                                alt="Image agrandie"
                                className="w-full h-full object-contain"
                            />
                            <button
                                onClick={() => setSelectedImage(null)}
                                className="absolute top-4 right-4 text-white p-2 hover:text-brandy-rose transition-colors"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-8 w-8"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </motion.div>
                    </div>
                )}
            </section>
        </div>
    );
};

export default ProjectDetail;